import React, { useState, useEffect, useMemo } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { initializeApp } from "firebase/app";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTag,
  faCheckCircle,
  faClock,
  faBan,
  faArrowRightFromBracket,
  faInfoCircle,
  faSearch,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Container,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
  Form,
  Badge,
  OverlayTrigger,
  Tooltip as BSTooltip,
} from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const firebaseConfig = {
  apiKey: "AIzaSyBBzepSPN2wdZfvan5GgyeeIREAV6k5BaU",
  authDomain: "loftie-4f472.firebaseapp.com",
  databaseURL: "https://loftie-4f472.firebaseio.com",
  projectId: "loftie-4f472",
  storageBucket: "loftie-4f472.appspot.com",
  messagingSenderId: "302939981606",
  appId: "1:302939981606:web:7fcb10ed6fe46fa437a3e7",
  measurementId: "G-L8VGQXX28P",
};

const app = initializeApp(firebaseConfig);
const authInstance = getAuth(app);
const db = getFirestore(app);

const C = {
  peach: "#F0D6B5",
  peachDark: "#E7AA7E",
  brown: "#635956",
  brownDark: "#3D3D3D",
  warmWhite: "#FBF7ED",
  green: "#10B981",
  coral: "#DA7833",
  red: "#CF6679",
  gray: "#9CA3AF",
  darkGray: "#4F4F4F",
  blue: "#4E6078",
  purple: "#312C6C",
  bg: "#FBF7ED",
  light: "#F6EFDF",
  white: "#FFFFFF",
  grid: "#E4E4E4",
};

const IGNORED_EMAILS = ["amandaranero@gmail.com"];

function isInternalEmail(email) {
  if (!email) return false;
  const lower = email.toLowerCase();
  return lower.endsWith("@byloftie.com") || IGNORED_EMAILS.includes(lower);
}

function toDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  if (v.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

function fmtDate(d) {
  if (!d) return "\u2014";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function monthKey(d) {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key) {
  const [y, m] = key.split("-");
  const names = [
    "Jan","Feb","Mar","Apr","May","Jun",
    "Jul","Aug","Sep","Oct","Nov","Dec",
  ];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

function typeLabel(type) {
  if (!type) return "Unknown";
  if (type === "referral") return "Referral";
  if (type === "loftie_plus: annual") return "Loftie+ Annual";
  if (type === "loftie_plus: lifetime") return "Loftie+ Lifetime";
  if (type === "loftie_plus: months") return "Loftie+ Monthly";
  if (type.startsWith("loftie_plus")) return "Loftie+";
  if (type.startsWith("percentage_off")) return "% Off";
  if (type.startsWith("amount_off")) return "$ Off";
  return type;
}

function typeBadgeColor(type) {
  if (!type) return C.gray;
  if (type === "referral") return C.brown;
  if (type === "loftie_plus: annual") return C.coral;
  if (type === "loftie_plus: lifetime") return "#06C270";
  if (type.startsWith("loftie_plus")) return C.peachDark;
  if (type.startsWith("percentage_off")) return "#D0BCAC";
  if (type.startsWith("amount_off")) return "#D0BCAC";
  return C.gray;
}

function KPICard({ icon, label, value, color }) {
  return (
    <Card
      style={{
        borderRadius: 12,
        border: "none",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        height: "100%",
      }}
    >
      <Card.Body style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <FontAwesomeIcon
            icon={icon}
            style={{ color: color || C.peach, marginRight: 8, fontSize: 13 }}
          />
          <span
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: C.gray,
              fontWeight: 600,
            }}
          >
            {label}
          </span>
        </div>
        <div
          style={{ fontSize: 28, fontWeight: 700, color: C.brown, lineHeight: 1.2 }}
        >
          {value}
        </div>
      </Card.Body>
    </Card>
  );
}

function PillToggle({ options, value, onChange }) {
  return (
    <div
      style={{
        display: "inline-flex",
        background: C.light,
        borderRadius: 8,
        padding: 2,
      }}
    >
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          style={{
            border: "none",
            borderRadius: 6,
            padding: "4px 12px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s ease",
            background: value === o.key ? C.peach : "transparent",
            color: value === o.key ? C.brownDark : C.gray,
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.grid}`,
        borderRadius: 8,
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        fontSize: 13,
      }}
    >
      <div style={{ color: C.gray, marginBottom: 4, fontSize: 12 }}>{label}</div>
      {payload.map((e, i) => (
        <div key={i} style={{ color: e.color }}>
          {e.name}: {e.value}
        </div>
      ))}
    </div>
  );
}

// Shows top N users, with a "show all" toggle
function UserList({ users }) {
  const [showAll, setShowAll] = useState(false);
  if (!users || users.length === 0) return <span style={{ color: C.gray }}>{"\u2014"}</span>;
  const display = showAll ? users : users.slice(0, 10);
  return (
    <div>
      {display.map((email, i) => (
        <div key={i} style={{ fontSize: 12, color: C.darkGray, lineHeight: 1.6 }}>
          {email}
        </div>
      ))}
      {users.length > 10 && (
        <button
          onClick={(e) => { e.stopPropagation(); setShowAll(!showAll); }}
          style={{
            background: "none",
            border: "none",
            color: C.coral,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            padding: 0,
            marginTop: 2,
          }}
        >
          {showAll ? "Show less" : `+${users.length - 10} more`}
        </button>
      )}
    </div>
  );
}

function DiscountAnalytics() {
  const [user, userLoading] = useAuthState(authInstance);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedEvents, setExpandedEvents] = useState({});
  const [showAllUngrouped, setShowAllUngrouped] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "discount-codes"));
        const rows = snap.docs.map((d) => {
          const data = d.data();
          const createdAt = toDate(data.created_at);
          const redeemedAt = toDate(data.redeemed_at);
          const isMultiUse = data.one_user === false;

          // Collect redeemed-by emails, filtering out internal
          let redeemedBy = [];
          if (isMultiUse) {
            redeemedBy = (data.users || []).filter((e) => !isInternalEmail(e));
          } else if (data.redeemed) {
            const email = data.email || data.user || "";
            if (email && !isInternalEmail(email)) {
              redeemedBy = [email];
            }
          }

          const redemptionCount = redeemedBy.length;
          const isRedeemed = redemptionCount > 0;

          return {
            id: d.id,
            event: data.event || null,
            type: data.type || "",
            createdAt,
            redeemedAt,
            isRedeemed,
            redemptionCount,
            redeemedBy,
            referrer: data.refferer || data.refferer_email || "",
            expired: !!data.expired,
            freeMonths: data.free_months || 1,
            isMultiUse,
          };
        });
        rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setCodes(rows);
        setError(null);
      } catch (e) {
        setError("Failed to load discount codes: " + e.message);
      }
      setLoading(false);
    })();
  }, [user]);

  // Top-level stats
  const stats = useMemo(() => {
    const total = codes.length;
    const redeemed = codes.filter((c) => c.isRedeemed).length;
    const expired = codes.filter((c) => c.expired).length;
    const active = total - redeemed - expired;
    const totalRedemptions = codes.reduce(
      (s, c) => s + c.redemptionCount,
      0
    );
    return { total, redeemed, expired, active, totalRedemptions };
  }, [codes]);

  // Group by event
  const eventGroups = useMemo(() => {
    const map = {};
    codes.forEach((c) => {
      if (!c.event) return;
      if (!map[c.event]) map[c.event] = [];
      map[c.event].push(c);
    });
    return Object.entries(map)
      .map(([event, items]) => {
        const total = items.length;
        const redeemed = items.filter((c) => c.isRedeemed).length;
        const totalRedemptions = items.reduce(
          (s, c) => s + c.redemptionCount,
          0
        );
        // Collect all unique redeemed-by emails across the event
        const allRedeemers = [];
        items.forEach((c) => c.redeemedBy.forEach((e) => {
          if (!allRedeemers.includes(e)) allRedeemers.push(e);
        }));
        const types = [...new Set(items.map((c) => c.type))];
        const hasMultiUse = items.some((c) => c.isMultiUse);
        return {
          event,
          items,
          total,
          redeemed,
          totalRedemptions,
          allRedeemers,
          types,
          hasMultiUse,
          rate: total ? Math.round((redeemed / total) * 100) : 0,
        };
      })
      .sort((a, b) => b.redeemed - a.redeemed || b.total - a.total);
  }, [codes]);

  // Codes with no event — redeemed first
  const ungrouped = useMemo(
    () =>
      codes
        .filter((c) => !c.event)
        .sort((a, b) => (b.isRedeemed ? 1 : 0) - (a.isRedeemed ? 1 : 0) || (b.createdAt || 0) - (a.createdAt || 0)),
    [codes]
  );

  // Monthly redemption trend
  const trendData = useMemo(() => {
    const byMonth = {};
    codes.forEach((c) => {
      if (!c.isRedeemed) return;
      const d = c.redeemedAt || c.createdAt;
      if (!d) return;
      const k = monthKey(d);
      if (!k) return;
      byMonth[k] = (byMonth[k] || 0) + c.redemptionCount;
    });
    return Object.keys(byMonth)
      .sort()
      .map((k) => ({ month: monthLabel(k), count: byMonth[k] }));
  }, [codes]);

  // Filter by search and status
  const matchesFilters = (c) => {
    if (statusFilter === "redeemed" && !c.isRedeemed) return false;
    if (statusFilter === "active" && (c.isRedeemed || c.expired)) return false;
    if (statusFilter === "expired" && !c.expired) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (c.event || "").toLowerCase().includes(q) ||
        c.referrer.toLowerCase().includes(q) ||
        c.type.toLowerCase().includes(q) ||
        c.redeemedBy.some((e) => e.toLowerCase().includes(q))
      );
    }
    return true;
  };

  const filteredEventGroups = useMemo(() => {
    if (!search && statusFilter === "all") return eventGroups;
    return eventGroups
      .map((g) => {
        const filtered = g.items.filter(matchesFilters);
        if (filtered.length === 0) return null;
        const redeemed = filtered.filter((c) => c.isRedeemed).length;
        const totalRedemptions = filtered.reduce(
          (s, c) => s + c.redemptionCount,
          0
        );
        const allRedeemers = [];
        filtered.forEach((c) => c.redeemedBy.forEach((e) => {
          if (!allRedeemers.includes(e)) allRedeemers.push(e);
        }));
        return {
          ...g,
          items: filtered,
          total: filtered.length,
          redeemed,
          totalRedemptions,
          allRedeemers,
          rate: filtered.length
            ? Math.round((redeemed / filtered.length) * 100)
            : 0,
        };
      })
      .filter(Boolean);
  }, [eventGroups, search, statusFilter]);

  const filteredUngrouped = useMemo(
    () => ungrouped.filter(matchesFilters),
    [ungrouped, search, statusFilter]
  );

  const toggleEvent = (event) => {
    setExpandedEvents((prev) => ({ ...prev, [event]: !prev[event] }));
  };

  // --- Render ---

  if (userLoading)
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "50vh" }}
      >
        <Spinner animation="border" style={{ color: C.peach }} />
      </Container>
    );

  if (!user)
    return (
      <div
        style={{
          background: C.bg,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          style={{
            width: 360,
            borderRadius: 12,
            border: "none",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Card.Body style={{ padding: 32 }}>
            <h4
              style={{
                color: C.peach,
                fontWeight: 700,
                marginBottom: 4,
                textAlign: "center",
              }}
            >
              <FontAwesomeIcon icon={faTag} style={{ marginRight: 8 }} />
              Discount Analytics
            </h4>
            <p
              style={{
                color: C.gray,
                fontSize: 13,
                textAlign: "center",
                marginBottom: 24,
              }}
            >
              Sign in to continue
            </p>
            {loginError && (
              <Alert variant="danger" style={{ fontSize: 13 }}>
                {loginError}
              </Alert>
            )}
            <Form
              onSubmit={async (e) => {
                e.preventDefault();
                setLoginError(null);
                try {
                  await signInWithEmailAndPassword(
                    authInstance,
                    loginEmail,
                    loginPassword
                  );
                } catch (err) {
                  setLoginError(err.message);
                }
              }}
            >
              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="Email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Control
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </Form.Group>
              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "none",
                  borderRadius: 8,
                  background: C.peach,
                  color: C.brownDark,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Sign In
              </button>
            </Form>
          </Card.Body>
        </Card>
      </div>
    );

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <Container style={{ maxWidth: 1100, paddingTop: 32, paddingBottom: 48 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <div>
            <h4 style={{ color: C.brown, fontWeight: 700, margin: 0 }}>
              <FontAwesomeIcon
                icon={faTag}
                style={{ color: C.peach, marginRight: 8 }}
              />
              Discount Code Analytics
            </h4>
            <p
              style={{ color: C.gray, fontSize: 13, margin: 0, marginTop: 4 }}
            >
              {stats.total} codes &middot; {stats.totalRedemptions} total
              redemptions
            </p>
          </div>
          <a
            href="/"
            style={{ color: C.gray, textDecoration: "none", fontSize: 13 }}
          >
            <FontAwesomeIcon
              icon={faArrowRightFromBracket}
              style={{ marginRight: 4 }}
            />{" "}
            Dashboard
          </a>
        </div>

        {error && (
          <Alert variant="warning" style={{ borderRadius: 10 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div
            className="d-flex justify-content-center"
            style={{ paddingTop: 80 }}
          >
            <Spinner animation="border" style={{ color: C.peach }} />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <Row className="g-3 mb-4">
              <Col xs={6} md={3}>
                <KPICard
                  icon={faTag}
                  label="Total Codes"
                  value={stats.total}
                  color={C.peach}
                />
              </Col>
              <Col xs={6} md={3}>
                <KPICard
                  icon={faCheckCircle}
                  label="Redeemed"
                  value={stats.redeemed}
                  color={C.green}
                />
              </Col>
              <Col xs={6} md={3}>
                <KPICard
                  icon={faClock}
                  label="Active"
                  value={stats.active}
                  color={C.coral}
                />
              </Col>
              <Col xs={6} md={3}>
                <KPICard
                  icon={faBan}
                  label="Expired"
                  value={stats.expired}
                  color={C.red}
                />
              </Col>
            </Row>

            {/* Filters */}
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              <PillToggle
                options={[
                  { key: "all", label: "All" },
                  { key: "redeemed", label: "Redeemed" },
                  { key: "active", label: "Active" },
                  { key: "expired", label: "Expired" },
                ]}
                value={statusFilter}
                onChange={setStatusFilter}
              />
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon
                  icon={faSearch}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 9,
                    color: C.gray,
                    fontSize: 12,
                  }}
                />
                <Form.Control
                  type="text"
                  placeholder="Search event, email, type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    borderRadius: 8,
                    fontSize: 12,
                    paddingLeft: 30,
                    width: 240,
                    height: 32,
                  }}
                />
              </div>
            </div>

            {/* Event Groups */}
            {filteredEventGroups.length > 0 && (
              <Card
                style={{
                  borderRadius: 12,
                  border: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  marginBottom: 24,
                }}
              >
                <Card.Body style={{ padding: 24 }}>
                  <h6
                    style={{
                      color: C.brown,
                      fontWeight: 600,
                      marginBottom: 16,
                    }}
                  >
                    By Event
                  </h6>
                  <Table hover size="sm" style={{ fontSize: 13 }}>
                    <thead>
                      <tr
                        style={{
                          color: C.gray,
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        <th></th>
                        <th>Event</th>
                        <th>Type</th>
                        <th>Codes</th>
                        <th>Redeemed</th>
                        <th>Rate</th>
                        <th>Total Redemptions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEventGroups.map((g) => (
                        <React.Fragment key={g.event}>
                          <tr
                            onClick={() => toggleEvent(g.event)}
                            style={{ cursor: "pointer" }}
                          >
                            <td style={{ width: 28 }}>
                              <FontAwesomeIcon
                                icon={
                                  expandedEvents[g.event]
                                    ? faChevronDown
                                    : faChevronRight
                                }
                                style={{ color: C.gray, fontSize: 10 }}
                              />
                            </td>
                            <td
                              style={{
                                fontWeight: 600,
                                color: C.brownDark,
                              }}
                            >
                              {g.event}
                            </td>
                            <td>
                              {g.types.map((t) => (
                                <Badge
                                  key={t}
                                  pill
                                  bg=""
                                  style={{
                                    background: typeBadgeColor(t),
                                    fontSize: 10,
                                    fontWeight: 600,
                                    marginRight: 4,
                                  }}
                                >
                                  {typeLabel(t)}
                                </Badge>
                              ))}
                            </td>
                            <td style={{ color: C.darkGray }}>{g.total}</td>
                            <td>
                              <span
                                style={{
                                  color: g.redeemed > 0 ? C.green : C.gray,
                                  fontWeight: 600,
                                }}
                              >
                                {g.redeemed}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  color:
                                    g.rate > 50
                                      ? C.green
                                      : g.rate > 0
                                      ? C.coral
                                      : C.gray,
                                  fontWeight: 600,
                                }}
                              >
                                {g.rate}%
                              </span>
                            </td>
                            <td style={{ color: C.darkGray }}>
                              {g.totalRedemptions}
                              {g.hasMultiUse && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <BSTooltip>
                                      Includes multi-use codes
                                    </BSTooltip>
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    style={{
                                      color: C.gray,
                                      marginLeft: 6,
                                      fontSize: 11,
                                      cursor: "help",
                                    }}
                                  />
                                </OverlayTrigger>
                              )}
                            </td>
                          </tr>
                          {expandedEvents[g.event] && (
                            <tr>
                              <td colSpan={7} style={{ background: C.light, padding: 16 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: C.brown, marginBottom: 8 }}>
                                  Redeemed by ({g.allRedeemers.length} users)
                                </div>
                                {g.allRedeemers.length === 0 ? (
                                  <div style={{ fontSize: 12, color: C.gray }}>No redemptions yet</div>
                                ) : (
                                  <UserList users={g.allRedeemers} />
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Redemptions Over Time */}
            {trendData.length > 0 && (
              <Card
                style={{
                  borderRadius: 12,
                  border: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  marginBottom: 24,
                }}
              >
                <Card.Body style={{ padding: 24 }}>
                  <h6
                    style={{
                      color: C.brown,
                      fontWeight: 600,
                      marginBottom: 16,
                    }}
                  >
                    Redemptions Over Time
                  </h6>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={trendData}
                      margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 11, fill: C.gray }}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: C.gray }}
                      />
                      <Tooltip content={<ChartTip />} />
                      <Bar
                        dataKey="count"
                        name="Redeemed"
                        fill={C.peach}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            )}

            {/* Other Codes (no event) — top 10 redeemed, expandable to all */}
            {filteredUngrouped.length > 0 && (
              <Card
                style={{
                  borderRadius: 12,
                  border: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                <Card.Body style={{ padding: 24 }}>
                  <h6
                    style={{
                      color: C.brown,
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    Other Codes
                    <span
                      style={{
                        fontSize: 12,
                        color: C.gray,
                        fontWeight: 400,
                        marginLeft: 8,
                      }}
                    >
                      {filteredUngrouped.length} codes without an event
                    </span>
                  </h6>
                  <div style={{ overflowX: "auto" }}>
                    <Table hover size="sm" style={{ fontSize: 13 }}>
                      <thead>
                        <tr
                          style={{
                            color: C.gray,
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          <th>Type</th>
                          <th>Status</th>
                          <th>Redeemed By</th>
                          <th>Created</th>
                          <th>Redeemed At</th>
                          <th>Free Months</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(showAllUngrouped
                          ? filteredUngrouped
                          : filteredUngrouped.slice(0, 10)
                        ).map((c) => (
                          <tr key={c.id}>
                            <td>
                              <Badge
                                pill
                                bg=""
                                style={{
                                  background: typeBadgeColor(c.type),
                                  fontSize: 10,
                                  fontWeight: 600,
                                }}
                              >
                                {typeLabel(c.type)}
                              </Badge>
                            </td>
                            <td>
                              {c.expired ? (
                                <span
                                  style={{ color: C.red, fontWeight: 600 }}
                                >
                                  Expired
                                </span>
                              ) : c.isRedeemed ? (
                                <span
                                  style={{ color: C.green, fontWeight: 600 }}
                                >
                                  Redeemed
                                  {c.isMultiUse
                                    ? ` (${c.redemptionCount})`
                                    : ""}
                                </span>
                              ) : (
                                <span style={{ color: C.coral }}>Active</span>
                              )}
                            </td>
                            <td style={{ color: C.darkGray }}>
                              <UserList users={c.redeemedBy} />
                            </td>
                            <td style={{ color: C.darkGray }}>
                              {fmtDate(c.createdAt)}
                            </td>
                            <td style={{ color: C.darkGray }}>
                              {fmtDate(c.redeemedAt)}
                            </td>
                            <td style={{ color: C.darkGray }}>
                              {c.freeMonths}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {filteredUngrouped.length > 10 && (
                    <button
                      onClick={() => setShowAllUngrouped(!showAllUngrouped)}
                      style={{
                        background: "none",
                        border: "none",
                        color: C.coral,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                        marginTop: 8,
                      }}
                    >
                      {showAllUngrouped
                        ? "Show top 10"
                        : `See all ${filteredUngrouped.length} codes`}
                    </button>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Container>
    </div>
  );
}

export default DiscountAnalytics;
